export type CsvDelimiter = "," | ";" | "\t";

export type CsvPreviewRecord = Record<string, string | null>;

export type ParsedCsvDiagnostics = {
  bomDetected: boolean;
  detectedDelimiter: CsvDelimiter;
  totalRows: number;
  rawFirstLines: string[];
  headerRowIndex: number;
  metadataRowsBeforeHeader: number;
  headerRowRaw: string[];
  normalizedHeaders: string[];
  rows: string[][];
  firstParsedRecords: CsvPreviewRecord[];
  columnsWithNumericValues: string[];
  candidateTimestampColumns: string[];
  candidateRespiratoryColumns: string[];
};

const TIMESTAMP_ALIASES = [
  "timestamp",
  "time",
  "date time",
  "datetime",
  "elapsed time",
  "seconds",
  "sample time"
];

const RESPIRATORY_ALIASES = [
  "minute ventilation",
  "ventilation",
  "ve",
  "ve l min",
  "ve l/min",
  "ventilation l min",
  "ventilation l/min",
  "breathing frequency",
  "breathing rate",
  "respiratory rate",
  "respiration rate",
  "br",
  "bf",
  "breaths per minute",
  "breaths/min",
  "rpm",
  "tidal volume",
  "vt",
  "tv",
  "tidal volume ml",
  "tidal volume l",
  "inspiratory time",
  "expiratory time",
  "respiratory duty cycle",
  "inhale exhale ratio",
  "respiratory intensity",
  "breathing efficiency",
  "ventilation threshold",
  "vt1",
  "vt2",
  "vo2 threshold",
  "respiratory threshold"
];

function unquote(value: string): string {
  const trimmed = value.trim();
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    return trimmed.slice(1, -1).trim();
  }
  return trimmed;
}

export function normalizeCsvHeader(value: string): string {
  return unquote(value)
    .toLowerCase()
    .replace(/[\u00a0]/g, " ")
    .replace(/[()\[\]{}]/g, " ")
    .replace(/[\\/_-]+/g, " ")
    .replace(/\s*\|\s*/g, " ")
    .replace(/\s+/g, " ")
    .replace(/[^a-z0-9.% ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function matchesAlias(header: string, alias: string): boolean {
  if (header === alias) return true;
  if (header.startsWith(`${alias} `)) return true;
  if (header.endsWith(` ${alias}`)) return true;
  return header.includes(` ${alias} `);
}

function parseDelimitedLine(line: string, delimiter: CsvDelimiter): string[] {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') {
      if (inQuotes && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (char === delimiter && !inQuotes) {
      values.push(unquote(current));
      current = "";
      continue;
    }
    current += char;
  }
  values.push(unquote(current));
  return values;
}

function detectDelimiter(lines: string[]): CsvDelimiter {
  const candidates: CsvDelimiter[] = [",", ";", "\t"];
  const scored = candidates.map(delimiter => {
    const score = lines.slice(0, 20).reduce((sum, line) => {
      const columnCount = parseDelimitedLine(line, delimiter).filter(Boolean).length;
      return sum + (columnCount >= 2 ? columnCount : 0);
    }, 0);
    return { delimiter, score };
  });
  scored.sort((left, right) => right.score - left.score);
  return scored[0]?.delimiter ?? ",";
}

function asNumber(value: string): number | null {
  const normalized = value.trim().replace(/,/g, "");
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function asTimestamp(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const parsed = Date.parse(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
}

function hasAnyAlias(header: string, aliases: string[]): boolean {
  return aliases.some(alias => matchesAlias(header, alias));
}

function computeHeaderScore(headers: string[], sampleRows: string[][]): number {
  if (headers.length < 2) return -1;
  const normalized = headers.map(normalizeCsvHeader).filter(Boolean);
  if (!normalized.length) return -1;

  const alphaCells = normalized.filter(cell => /[a-z]/.test(cell)).length;
  const uniqueCells = new Set(normalized).size;
  const recognized = normalized.filter(cell => hasAnyAlias(cell, [...TIMESTAMP_ALIASES, ...RESPIRATORY_ALIASES])).length;

  let rowConsistency = 0;
  let dataLike = 0;
  for (const row of sampleRows) {
    if (!row.length) continue;
    if (Math.abs(row.length - headers.length) <= 1) rowConsistency += 1;
    const nonEmpty = row.map(cell => cell.trim()).filter(Boolean);
    if (!nonEmpty.length) continue;
    const numericOrTime = nonEmpty.filter(cell => asNumber(cell) !== null || asTimestamp(cell) !== null).length;
    if (numericOrTime / nonEmpty.length >= 0.4) dataLike += 1;
  }

  return alphaCells * 2 + uniqueCells * 0.3 + recognized * 3 + rowConsistency * 1.5 + dataLike * 1.5;
}

export function parseCsvDiagnostics(text: string): ParsedCsvDiagnostics {
  const bomDetected = text.charCodeAt(0) === 0xfeff;
  const noBom = bomDetected ? text.slice(1) : text;
  const rawLines = noBom.split(/\r?\n/);
  const rawFirstLines = rawLines.slice(0, 5).map(line => line.replace(/\r/g, ""));

  const indexedNonEmpty = rawLines
    .map((line, index) => ({ line: line.trim(), index }))
    .filter(item => item.line.length > 0);

  if (!indexedNonEmpty.length) {
    return {
      bomDetected,
      detectedDelimiter: ",",
      totalRows: 0,
      rawFirstLines,
      headerRowIndex: 0,
      metadataRowsBeforeHeader: 0,
      headerRowRaw: [],
      normalizedHeaders: [],
      rows: [],
      firstParsedRecords: [],
      columnsWithNumericValues: [],
      candidateTimestampColumns: [],
      candidateRespiratoryColumns: []
    };
  }

  const lines = indexedNonEmpty.map(item => item.line);
  const detectedDelimiter = detectDelimiter(lines);
  const splitRows = lines.map(line => parseDelimitedLine(line, detectedDelimiter));

  let bestHeader = 0;
  let bestScore = Number.NEGATIVE_INFINITY;
  const searchLimit = Math.min(splitRows.length, 25);
  for (let index = 0; index < searchLimit; index += 1) {
    const candidate = splitRows[index];
    const sampleRows = splitRows.slice(index + 1, index + 4);
    const score = computeHeaderScore(candidate, sampleRows);
    if (score > bestScore) {
      bestScore = score;
      bestHeader = index;
    }
  }

  const headerRowRaw = splitRows[bestHeader] ?? [];
  const normalizedHeaders = headerRowRaw.map((header, index) => {
    const normalized = normalizeCsvHeader(header);
    return normalized || `column_${index + 1}`;
  });

  const rows = splitRows.slice(bestHeader + 1)
    .map(row => {
      const sized = Array.from({ length: normalizedHeaders.length }, (_, index) => row[index] ?? "");
      return sized;
    })
    .filter(row => row.some(cell => cell.trim().length > 0));

  const firstParsedRecords: CsvPreviewRecord[] = rows.slice(0, 3).map(row => {
    const record: CsvPreviewRecord = {};
    normalizedHeaders.forEach((header, index) => {
      record[header] = row[index]?.trim() || null;
    });
    return record;
  });

  const columnsWithNumericValues = normalizedHeaders.filter((header, index) => {
    const values = rows.slice(0, 500).map(row => asNumber(row[index])).filter((value): value is number => value !== null);
    return values.length >= Math.max(3, Math.round(rows.length * 0.1));
  });

  const candidateTimestampColumns = normalizedHeaders.filter((header, index) => {
    if (hasAnyAlias(header, TIMESTAMP_ALIASES)) return true;
    const values = rows.slice(0, 500).map(row => asTimestamp(row[index])).filter((value): value is number => value !== null);
    return values.length >= Math.max(3, Math.round(rows.length * 0.5));
  });

  const candidateRespiratoryColumns = normalizedHeaders.filter(header => hasAnyAlias(header, RESPIRATORY_ALIASES));

  return {
    bomDetected,
    detectedDelimiter,
    totalRows: rows.length,
    rawFirstLines,
    headerRowIndex: indexedNonEmpty[bestHeader]?.index ?? 0,
    metadataRowsBeforeHeader: bestHeader,
    headerRowRaw,
    normalizedHeaders,
    rows,
    firstParsedRecords,
    columnsWithNumericValues,
    candidateTimestampColumns,
    candidateRespiratoryColumns
  };
}
